import { useDeleteCommentMutation, useUpdateCommentContentMutation } from 'api/collaborateApi';
import { formatDistanceStrict } from 'date-fns';
import { AtarimEditorRef, Editor } from 'editor';
import { Check, ChevronDown, EllipsisVertical, Eye, EyeOff, Lightbulb, LightbulbIcon, Pen, Sparkles, Trash2, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommentClarification, CommentFile, Comment as CommentType } from 'types/api/tasks';
import { Button, Collapsible, DropdownMenu, ResponsiveDialog, Skeleton, Tooltip } from 'ui';
import { Avatar } from 'ui/components/Avatar';
import { cn } from 'utils/cn';
import { formatDateWithIntl } from '../../../../hooks/use-format-date';
import { useAppSelector } from '../../../../store';
import { getSelectedSiteCollaborateInfo } from '../../../../store/siteInfoSlice';
import { getSelectedTaskId } from '../../../../store/tasksSlice';
import { getUser } from '../../../../store/userSlice';
import { FileDisplay } from '../file/file';
import ClaritySelectToggle from './multiselect-toggle';

interface Props {
  details: CommentType;
}

// Constants
const CLARITY_SCORE_THRESHOLD = 0.5;
const CLARO_AVATAR_URL = 'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/claro.png';

// Types for clarity status
type ClarityStatus = 'pending' | 'accepted' | 'dismissed';

export const Comment = ({ details }: Props) => {

  const taskId = useAppSelector(getSelectedTaskId);
  const user = useAppSelector(getUser);
  const collaborateInfo = useAppSelector(getSelectedSiteCollaborateInfo);
  const commentEditorRef = useRef<AtarimEditorRef | null>(null);
  const [editable, setEditable] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [updateComment, { isLoading: isUpdatingComment }] = useUpdateCommentContentMutation();

  // Memoized computations
  const hasPermission = useMemo(() => {
    const role = collaborateInfo?.result?.role;
    return role === 'admin' || role === 'contributor' || role === 'collaborator';
  }, [collaborateInfo?.result?.role]);

  const isRemovable = useMemo(() =>
    user.role === 'admin' || user.id === details.id,
    [user.role, user.id, details.id]
  );

  const filesByType = useMemo(() => {
    if (!details.files) return { imageFiles: [], otherFiles: [] };

    return {
      imageFiles: details.files.filter(f => f.type?.startsWith('image/')),
      otherFiles: details.files.filter(f => !f.type?.startsWith('image/'))
    };
  }, [details.files]);

  const clarification = details.comment_clarification;
  // Changed logic: now checking if score is BELOW threshold (unclear)
  const isLowClarityScore = clarification && clarification.clarity_score < CLARITY_SCORE_THRESHOLD;

  // Handlers
  const handleUpdateCommentContent = useCallback(async () => {
    if (!commentEditorRef.current || !taskId || !details?.id) return;

    const content = commentEditorRef.current.getHTML();
    if (!content) return;

    commentEditorRef.current.editor?.commands.clearContent();
    await updateComment({
      task_id: taskId,
      comment_id: details.id,
      comment_type: 'normal_text',
      comment_content: content,
    });
    setEditable(false);
  }, [updateComment, taskId, details?.id]);

  const toggleEditable = useCallback(() => setEditable(prev => !prev), []);

  useEffect(() => {
    setEditable(false);
  }, [details]);

  if (!details?.id) return null;

  return (
    <Fragment key={`comment-${details.id}-${details.updated_at}`}>
      <CommentMain
        details={details}
        taskId={taskId ?? ''}
        editable={editable}
        isUpdatingComment={isUpdatingComment}
        hasPermission={hasPermission}
        commentEditorRef={commentEditorRef}
        filesByType={filesByType}
        isRemovable={isRemovable}
        onToggleEditable={toggleEditable}
        onUpdateComment={handleUpdateCommentContent}
        onDeleteOpen={() => setOpenDelete(true)}
      />

      <ClarificationSection
        key={`clarification-${details.id}`}
        clarification={clarification}
        details={details}
        isLowClarityScore={isLowClarityScore}
      />

      <DeleteCommentModal
        taskId={taskId ?? ''}
        commentId={details.id}
        open={openDelete}
        setOpen={setOpenDelete}
      />
    </Fragment>
  );
};

// Separate component for main comment content
interface CommentMainProps {
  details: CommentType | Partial<CommentType>;
  taskId: string;
  editable: boolean;
  isUpdatingComment: boolean;
  hasPermission: boolean;
  commentEditorRef: React.RefObject<AtarimEditorRef | null>;
  filesByType: { imageFiles: CommentFile[]; otherFiles: CommentFile[] };
  isRemovable: boolean;
  onToggleEditable: () => void;
  onUpdateComment: () => void;
  onDeleteOpen: () => void;
}

const CommentMain = ({
  details,
  taskId,
  editable,
  isUpdatingComment,
  hasPermission,
  commentEditorRef,
  filesByType,
  isRemovable,
  onToggleEditable,
  onUpdateComment,
  onDeleteOpen
}: CommentMainProps) => {
  const { t } = useTranslation();

  return (
    <div data-no-drag className={cn('flex flex-col gap-2 bg-background', details.is_note === 1 && 'border border-ata-orange bg-ata-orange/5 p-2 rounded-xl')}>
      <div className="flex gap-2 overflow-x-hidden">
        <Avatar
          src={details.avatar}
          email={details.email}
          title={details.user_name || t('comment.labels.guest', 'Guest')}
          alt={details.user_name}
          id={typeof details.user_normal_id === 'number' ? details.user_normal_id : parseInt(details.user_normal_id ?? '0')}
          borderRadius="lg"
        />
        <div className="w-full relative flex flex-col gap-1">
          <CommentHeader
            details={details}
            isUpdatingComment={isUpdatingComment}
            hasPermission={hasPermission}
            editable={editable}
            onToggleEditable={onToggleEditable}
            onDeleteOpen={onDeleteOpen}
          />

          <Editor
            data-no-drag
            key={editable ? 'editable' : 'readonly'}
            ref={commentEditorRef}
            content={details.comments}
            editable={editable}
            onSubmit={onUpdateComment}
            showToolbar={false}
            showBubbleMenu={true}
            showLinkPreview={false}
            className='text-sm w-full break-words hyphens-auto pr-4 text-textcolor'
          />

          <EditActions
            editable={editable}
            onToggleEditable={onToggleEditable}
            onUpdateComment={onUpdateComment}
          />
        </div>
      </div>

      <FileSection
        files={details.files}
        filesByType={filesByType}
        isRemovable={isRemovable}
        commentId={details.id}
        taskId={taskId}
      />
    </div>
  );
};

// Comment header with status indicators and actions
interface CommentHeaderProps {
  details: CommentType | Partial<CommentType>;
  isUpdatingComment: boolean;
  hasPermission: boolean;
  editable: boolean;
  onToggleEditable: () => void;
  onDeleteOpen: () => void;
}

const CommentHeader = ({ details, isUpdatingComment, hasPermission, editable, onToggleEditable, onDeleteOpen }: CommentHeaderProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex h-5">
      <div className='flex grow gap-1 items-center'>
        <div className="items-center font-medium text-textcolor text-sm max-w-[220px] truncate flex gap-1">
          {details.user_name}
          {details.is_note === 1 && (
            <Tooltip content={t('comment.tooltip.internal_note', 'Visible to Admins and Contributors only')}>
              <Eye size={16} aria-hidden="true" />
            </Tooltip>
          )}
        </div>

        <CommentStatusIndicators details={details} isUpdatingComment={isUpdatingComment} />
      </div>

      {hasPermission && (
        <CommentActionsMenu
          details={details}
          editable={editable}
          onToggleEditable={onToggleEditable}
          onDeleteOpen={onDeleteOpen}
        />
      )}
    </div>
  );
};

// Status indicators (edited, timestamp, clarity)
const CommentStatusIndicators = ({ details, isUpdatingComment }: { details: CommentType | Partial<CommentType>; isUpdatingComment: boolean }) => {
  const { t } = useTranslation();

  return (
    <>
      {details.is_edited && (
        <span className="font-base flex flex-row items-center gap-1 self-center text-xs leading-none text-gray-400">
          {t('comment.status.edited', 'Edited')}
        </span>
      )}

      {isUpdatingComment ? (
        <div className="text-xs text-gray-400 tracking-normal">
          {t('comment.status.updating', 'Updating comment...')}
        </div>
      ) : (
        <Tooltip content={formatDateWithIntl(details.updated_at ?? '')}>
          <div className='text-xs text-gray-400 tracking-normal'>
            {!details.days?.includes("posting") ?
              formatDistanceStrict(new Date(details.updated_at ?? ''), new Date(), { addSuffix: true }) :
              details.days}
          </div>
        </Tooltip>
      )}

      <ClarityIndicator clarification={details.comment_clarification} />
    </>
  );
};

// Clarity score indicator
const ClarityIndicator = ({ clarification }: { clarification?: CommentClarification }) => {
  const { t } = useTranslation();

  if (!clarification) return null;

  const getIndicatorProps = () => {
    const { clarity_score, status } = clarification;

    // Changed logic: high score (>= 0.5) means clear
    if (clarity_score >= CLARITY_SCORE_THRESHOLD) {
      return {
        className: "bg-ata-green",
        tooltip: t('comment.tooltip.clarity_score', 'Crystal clear! Claro gave this the thumbs-up for clarity.')
      };
    }

    // Low score (< 0.5) means unclear, so we show different statuses
    if (status === 'pending') {
      return {
        className: "bg-ata-purple",
        tooltip: t('comment.tooltip.clarity_score', 'This comment could use some clarity. Claro is on it!')
      };
    }

    if (status === 'accepted') {
      return {
        className: "bg-gradient-to-r from-[#3ED696] to-[#8F5DF3]",
        tooltip: t('comment.tooltip.clarity_score', "Updated with Claro's suggestion for better clarity.")
      };
    }

    if (status === 'dismissed') {
      return {
        className: "bg-ata-orange",
        tooltip: t('comment.tooltip.clarity_score', 'This comment was flagged for clarity. The user reviewed and dismissed all suggested edits.')
      };
    }

    return null;
  };

  const props = getIndicatorProps();
  if (!props) return null;

  return (
    <Tooltip content={props.tooltip}>
      <div className={cn(props.className, "rounded-[50%_18%_50%_50%] p-0.5")}>
        <Lightbulb size={12} className="text-white" />
      </div>
    </Tooltip>
  );
};

// Actions dropdown menu
const CommentActionsMenu = ({ details, editable, onToggleEditable, onDeleteOpen }: {
  details: CommentType | Partial<CommentType>;
  editable: boolean;
  onToggleEditable: () => void;
  onDeleteOpen: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <DropdownMenu modal={false}>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="right-0 -top-2 ml-0 h-9 w-9 min-w-0 p-0 absolute hover:bg-transparent"
          tooltip={t('comment.tooltip.actions', 'Actions')}
          tooltipSide="top"
          toolTipPortal={true}
        >
          <EllipsisVertical size={18} aria-hidden="true" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="w-fit min-w-0" align="center" side="left" portal={false}>
        <DropdownMenu.Item onSelect={onToggleEditable}>
          {editable ? <X size={18} /> : <Pen size={18} />}
          {editable ? t('comment.actions.cancel', 'Cancel') : t('comment.actions.edit', 'Edit')}
        </DropdownMenu.Item>
        <DropdownMenu.Item
          className="focus:text-white focus:bg-destructive"
          onSelect={onDeleteOpen}
        >
          <Trash2 size={18} aria-hidden="true" />
          {t('comment.actions.delete', 'Delete')}
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

// Edit actions (save/cancel buttons)
const EditActions = ({ editable, onToggleEditable, onUpdateComment }: {
  editable: boolean;
  onToggleEditable: () => void;
  onUpdateComment: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <AnimatePresence>
      {editable && (
        <motion.div
          initial={{ opacity: 0, y: -10, marginTop: 0, touchAction: 'none' }}
          animate={{ opacity: 1, y: 0, marginTop: 5 }}
          exit={{ opacity: 0, y: -10, marginTop: 0, touchAction: 'none', overflow: 'hidden' }}
          transition={{ duration: 0.2, type: 'tween', ease: 'easeOut' }}
          className='flex gap-1 flex-row justify-end pr-4'
        >
          <Button
            size="icon"
            variant="destructive-outline"
            tooltip={t('comment.edit.discard', 'Discard changes')}
            onClick={onToggleEditable}
            leftIcon={<X size={18} />}
            className='text-destructive'
          />
          <Button
            size="icon"
            tooltip={t('comment.edit.save', 'Save changes')}
            onClick={onUpdateComment}
            leftIcon={<Check size={18} />}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// File display section
const FileSection = ({ files, filesByType, isRemovable, commentId, taskId }: {
  files?: CommentFile[];
  filesByType: { imageFiles: CommentFile[]; otherFiles: CommentFile[] };
  isRemovable: boolean;
  commentId?: string;
  taskId: string;
}) => {
  if (!files?.length || !commentId) return null;

  const { imageFiles, otherFiles } = filesByType;

  return (
    <div id={`comment-files-${commentId}`} className="flex flex-col gap-2">
      {imageFiles.length > 0 && (
        <div className={`grid ${imageFiles.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
          {imageFiles.map((f) => (
            <FileDisplay
              key={f.id}
              removable={isRemovable}
              downloadable
              file={f}
              commentId={commentId}
              taskId={taskId}
              singleImage={imageFiles.length === 1}
            />
          ))}
        </div>
      )}
      {otherFiles.length > 0 && (
        <div className="grid grid-cols-1 gap-2">
          {otherFiles.map((f) => (
            <FileDisplay
              key={f.id}
              removable={isRemovable}
              downloadable
              file={f}
              commentId={commentId}
              taskId={taskId}
            />
          ))}
        </div>
      )}
    </div>
  );
};


const AnimateIn = ({ children, keyId, delay = 0 }: { children: React.ReactNode; keyId: string, delay?: number }) => (
  <AnimatePresence mode="wait">
    <motion.div
      key={keyId}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1], delay }}
    >
      {children}
    </motion.div>
  </AnimatePresence>
);

// Clarification section component
const ClarificationSection = ({ clarification, details, isLowClarityScore }: {
  clarification?: CommentClarification;
  details: CommentType;
  isLowClarityScore?: boolean;
}) => {
  const status = useMemo(() => clarification?.status as ClarityStatus, [clarification?.status]);

  if (!clarification) return null;
  // Accepted
  if (status === 'accepted') {
    return (
      <AnimateIn keyId="accepted">
        <ClarificationAccepted clarification={clarification} />
      </AnimateIn>
    );
  }

  // Dismissed
  if (status === 'dismissed') {
    return (
      <AnimateIn keyId="dismissed">
        <ClarificationDismissed details={details} clarification={clarification} />
      </AnimateIn>
    );
  }

  // Pending with options
  if (status === 'pending' && isLowClarityScore) {
    return (
      <AnimateIn keyId="options">
        <ClarificationPendingOptions details={details} clarification={clarification} isLowClarityScore={isLowClarityScore} />
      </AnimateIn>
    );
  }

  return null;
};

const ClarificationPendingOptions = ({ details, clarification, isLowClarityScore }: {
  details: CommentType;
  clarification: CommentClarification;
  isLowClarityScore?: boolean;

}) => (

  <div className='flex flex-col gap-2 border border-ata-purple p-2 rounded-xl' key={clarification.id}>
    <ClaroHeader
      content={
        clarification.options && isLowClarityScore ?
          `<p>Hmm.. This is pretty vague <b>${details.user_name?.split(' ')[0]}</b>...<br/>Maybe some of these would be clearer?</p>`
          :
          `<p>Hmm.. This is pretty vague <b>${details.user_name?.split(' ')[0]}</b>...<br/>Let me have a quick look for you</p>`
      }
    />
    <AnimatePresence mode="popLayout">
      {!clarification.options && isLowClarityScore && (
        <motion.div
          key="loading"
          initial={{ opacity: 0, y: -10, height: 0, marginTop: 4 }}
          animate={{ opacity: 1, y: 0, height: 'auto', marginTop: 4 }}
          exit={{ opacity: 0, y: -10, height: 0, marginTop: 4, transition: { duration: 0.1, ease: "easeInOut" } }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <Skeleton className="h-8 w-full" />
        </motion.div>
      )}
      {clarification.options && (
        <motion.div
          key="options"
          initial={{ opacity: 0, y: -100, height: 0, marginTop: 4 }}
          animate={{ opacity: 1, y: 0, height: 'auto', marginTop: 4 }}
          exit={{ opacity: 0, y: -100, height: 0, marginTop: 4, }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <ClaritySelectToggle
            commentId={details.id}
            feedback={details.comments}
            options={clarification.options}
          />
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const ClarificationAccepted = ({ clarification }: { clarification: CommentClarification }) => (
  <div className='flex flex-col gap-1' key={clarification.id}>
    <div className="flex">
      <Avatar
        src={CLARO_AVATAR_URL}
        alt="claro"
        borderRadius="lg"
      />
      <div className="flex flex-col gap-1 ml-2">
        <div className="flex justify-between">
          <div className="flex items-center gap-1">
            <p className='font-medium text-textcolor text-sm'>Claro</p>
            <Tooltip content={formatDateWithIntl(new Date(clarification.acted_at * 1000))}>
              <div className='text-xs text-gray-400 tracking-normal'>
                {formatDistanceStrict(new Date(clarification.acted_at * 1000), new Date(), { addSuffix: true })}
              </div>
            </Tooltip>
          </div>
          <span className='flex text-xs uppercase bg-gradient-to-tr from-[#3ED696] to-[#8F5DF3] px-1.5 rounded-md text-white items-center gap-1 self-end font-semibold'>
            <Sparkles size={10} /> Clarity
          </span>
        </div>
        <Editor
          data-no-drag
          key={'clarification'}
          editable={false}
          content={clarification.clarification_text}
          showToolbar={false}
          showBubbleMenu={true}
          showLinkPreview={false}
          className='text-sm'
        />
      </div>
    </div>
  </div>
);

const ClarificationDismissed = ({ details, clarification }: {
  details: CommentType | Partial<CommentType>;
  clarification: CommentClarification;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className='flex flex-col gap-1 border border-ata-orange bg-ata-orange/5 p-2 rounded-xl' key={clarification.id}>
      <div className="flex">
        <Avatar src={CLARO_AVATAR_URL} alt="claro" borderRadius="lg" />
        <div className="flex flex-col gap-1 ml-2">
          <div className='flex items-center gap-1'>
            <p className='font-medium text-textcolor text-sm'>Claro</p>
            <Tooltip content={t('comment.tooltip.internal_note', 'Visible to Admins and Contributors only')}>
              <Eye size={16} aria-hidden="true" />
            </Tooltip>
            <Tooltip content={formatDateWithIntl(new Date(clarification.acted_at * 1000))}>
              <div className='text-xs text-gray-400 tracking-normal'>
                {formatDistanceStrict(new Date(clarification.acted_at * 1000), new Date(), { addSuffix: true })}
              </div>
            </Tooltip>
          </div>
          <Editor
            data-no-drag
            key={'clarification'}
            editable={false}
            content={`<p>This comment was flagged for clarity, but <b>${details.user_name?.split(' ')[0]}</b> chose to keep it as originally written.</p>`}
            showToolbar={false}
            showBubbleMenu={true}
            showLinkPreview={false}
            className='text-sm'
          />
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <Collapsible.Trigger asChild>
              <Button
                variant="ghost"
                className="p-0 hover:bg-transparent -mt-1"
                rightIcon={
                  <motion.span
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown size={16} />
                  </motion.span>
                }
                size="sm"
              >
                {t('comment.clarification.dismissed.show_details', 'Show Suggestions')}
              </Button>
            </Collapsible.Trigger>
            <AnimatePresence >
              {isOpen && (
                <Collapsible.Content asChild className='mb-2' forceMount>
                  <motion.div
                    key="collapsible-content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    className="overflow-hidden"
                  >
                    <motion.ul
                      key="list"
                      initial="hidden"
                      animate={isOpen ? 'visible' : 'hidden'}
                      variants={{
                        visible: {
                          transition: {
                            delayChildren: 0.2,
                            staggerChildren: 0.15,
                          },
                        },
                        hidden: {},
                      }}
                      className="text-sm text-textcolor flex flex-col gap-1"
                    >
                      <AnimatePresence mode="wait">
                        {clarification.options?.map((option) => (
                          <motion.li
                            key={option.id}
                            variants={{
                              hidden: { opacity: 0, y: -10 },
                              visible: {
                                opacity: 1,
                                y: 0,
                                transition: {
                                  duration: 0.4,
                                  ease: [0.4, 0, 0.2, 1],
                                },
                              },
                            }}
                            className="flex items-start gap-2"
                          >
                            <LightbulbIcon size={16} className='h-full min-w-4 w-full max-w-4 mt-0.5' />
                            <span dangerouslySetInnerHTML={{ __html: option.text }} />
                          </motion.li>
                        ))}
                      </AnimatePresence>
                    </motion.ul>
                  </motion.div>


                </Collapsible.Content>
              )}
            </AnimatePresence>
          </Collapsible>
        </div>
      </div>
    </div>
  );
};

// Shared Claro header component
const ClaroHeader = ({ content }: { content: string }) => {
  return (
    <div className="flex" key={content}>
      <Avatar src={CLARO_AVATAR_URL} alt="claro" borderRadius="lg" />
      <div className="flex flex-col gap-1 ml-2">
        <div className='flex items-center gap-1'>
          <p className='font-medium text-textcolor text-sm'>Claro</p>
          <EyeOff size={16} className='text-gray-400' />
          <p className='text-xs text-gray-400 tracking-normal'>Only visible to you</p>
        </div>
        <Editor
          data-no-drag
          key={'clarification'}
          editable={false}
          content={content}
          showToolbar={false}
          showBubbleMenu={true}
          showLinkPreview={false}
          className='text-sm'
        />
      </div>
    </div>
  );
};

// Delete modal component
function DeleteCommentModal({ open, setOpen, taskId, commentId }: {
  taskId?: string;
  commentId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [deleteComment, { isLoading }] = useDeleteCommentMutation();

  const handleDelete = useCallback(async () => {
    if (!commentId || !taskId) return;
    await deleteComment({ task_id: taskId, comment_id: commentId });
    setOpen(false);
  }, [deleteComment, commentId, taskId, setOpen]);

  return (
    <ResponsiveDialog open={open} onOpenChange={setOpen}>
      <ResponsiveDialog.Content
        aria-describedby={undefined}
        className='z-50'
        container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
      >
        <ResponsiveDialog.Header>
          <ResponsiveDialog.Title>
            {t('comment.delete_dialog.title', 'Delete Comment')}
          </ResponsiveDialog.Title>
        </ResponsiveDialog.Header>
        <ResponsiveDialog.Description className="flex flex-col gap-2">
          <p>{t('comment.delete_dialog.confirm', 'Are you sure you want to delete this comment?')}</p>
          <p className="text-ata-pink">{t('comment.delete_dialog.warning', 'This action cannot be undone.')}</p>
        </ResponsiveDialog.Description>
        <ResponsiveDialog.Footer className="flex gap-2 pr-2">
          <Button
            variant="destructive"
            size="sm"
            loading={isLoading}
            onClick={handleDelete}
          >
            {t('comment.delete_dialog.delete_button', 'Delete Comment')}
          </Button>
          <ResponsiveDialog.Close asChild>
            <Button variant="secondary" size="sm" disabled={isLoading}>
              {t('comment.delete_dialog.cancel_button', 'Cancel')}
            </Button>
          </ResponsiveDialog.Close>
        </ResponsiveDialog.Footer>
      </ResponsiveDialog.Content>
    </ResponsiveDialog>
  );
}